import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleTwoTone,
  EditTwoTone,
  EyeTwoTone,
  FileAddTwoTone,
  GlobalOutlined,
  LinkOutlined,
  SendOutlined,
  UserSwitchOutlined,
  CalendarOutlined,
  MailOutlined,
  SwapOutlined,
  UndoOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Modal,
  notification,
  Popover,
  Row,
  Skeleton,
  Space,
  Tabs,
  Typography,
} from "antd";
import { isEmpty, isNil, values } from "lodash";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import openLinkInNewTab from "helpers/openLinkInNewTab";
import { ValidateAndGetLandwirtData } from "types/masterdata.types";
import {
  useGetErhebungsdatenPersonDataQuery,
  useGetLandwirtKundensatzQuery,
  useGetTreuhaenderQuery,
  useGetTreuhandstelleQuery,
  useResetKundensatzMutation,
  useSetOnboardingCodeMutation,
} from "store/masterdata";
import { FiltersValues } from "components/elements/DataTable/DataTable";
import ChangeLieferterminModal from "views/App/views/Masterdata/components/LWDetailsModal/ChangeLieferterminModal";
import { Roles } from "types/user.types";
import getAuthority from "utils/getAuthority";
import ChangeTHContactEmail from "views/App/views/Masterdata/components/LWDetailsModal/ChangeTHEmail";
import {
  useSendAccessDataMutation,
  useSendAkbLetterMutation,
  useSendBlFbLetterMutation,
} from "store/dispatch";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal";
import formatDateTime from "helpers/formatDateTime";
import { useLazyGetEnumInfoQuery } from "store/system";
import SetRekSubStatusModal from "views/App/views/Masterdata/components/LWDetailsModal/SetRekSubStatusModal";
import THInfoModal from "../THInfoModal";
import THsInfoModal from "../THsInfoModal";
import UpdateLandwirtForm from "./Forms/UpdateLandwirtForm";
import ValidationForm from "./Forms/ValidationForm";
import styles from "./LWDetailsModal.module.less";
import LWLanguageForm from "./Forms/LWLanguageForm";
import HistoryTab from "../HistoryTab";
import ChangeTHsModal from "./ChangeTHsModal";

const { Title } = Typography;

export enum LWDetailsModals {
  LandwirtForm = "LandwirtForm",
  THInfos = "THInfos",
  THsInfos = "THsInfos",
  LWLanguageFormModal = "LWLanguageFormModal",
}

export enum LWDetailsTabs {
  Kundeninfo = "KundenInfo",
  History = "History",
  DatenkontrolleHistory = "DatenkontrolleHistory",
}

export interface LWDetailsProps {
  visible: boolean;
  kundennummer?: number;
  defaultActiveTab?: LWDetailsTabs;
  modalOpenedName?: LWDetailsModals;
  rekStatusFilters?: FiltersValues;
  onClose(clearQuery?: boolean): void;
}
const { TabPane } = Tabs;

function LWDetailsModal({
  visible,
  kundennummer,
  defaultActiveTab,
  modalOpenedName,
  onClose,
  rekStatusFilters,
}: LWDetailsProps): JSX.Element | null {
  if (!kundennummer) {
    return null;
  }
  const { t } = useTranslation();
  const roles = getAuthority();

  const isBtnNachkodierungVisible = roles.some(
    (role) => role === Roles.LinkAdmin || role === Roles.LinkPL
  );

  const isAdminBtnVisible = roles.some(
    (role) =>
      role === Roles.LinkAdmin ||
      role === Roles.LinkFeldleitung ||
      role === Roles.LinkPL
  );

  const isBtnVisible = roles.some(
    (role) =>
      role === Roles.LinkPL ||
      role === Roles.LinkFeldleitung ||
      role === Roles.LinkAdmin ||
      role === Roles.LinkAgent
  );

  const isSprachwechselBtnVisible = roles.some(
    (role) =>
      role === Roles.LinkPL ||
      role === Roles.LinkFeldleitung ||
      role === Roles.LinkAdmin ||
      role === Roles.LinkAgent ||
      role === Roles.FachlicheHotline
  );

  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [landwirtFormVisible, setLandwirtFormVisible] = useState(false);
  const [tHInfoModalVisible, setTHInfosModalVisible] = useState(false);
  const [tHsInfoModalVisible, setTHsInfosModalVisible] = useState(false);
  const [lWLanguageFormVisible, setLWLanguageFormVisible] = useState(false);
  const [changeTHsModalVisible, setChangeTHsModalVisible] = useState(false);
  const [changeLieferterminModalVisible, setChangeLieferterminModalVisible] =
    useState(false);
  const [rekSubStatusModalVisible, setRekSubStatusModalVisible] =
    useState(false);
  const [nachkodierungModalVisible, setNachkodierungModalVisible] =
    useState(false);
  const [changeTHContactMailVisible, setChangeTHContactMailVisible] =
    useState(false);
  const [resetConfirmationModalVisible, setResetConfirmationModalVisible] =
    useState(false);
  const [
    setNewOnboardingCodeLwModalVisible,
    setSetNewOnboardingCodeLwModalVisible,
  ] = useState(false);
  const [
    sendAccessDataConfirmationModalVisible,
    setSendAccessDataConfirmationModalVisible,
  ] = useState(false);
  const [
    sendAkbLetterConfirmationModalVisible,
    setSendAkbLetterConfirmationModalVisible,
  ] = useState(false);
  const [sendAccessDataForTreuhaender, setSendAccessDataForTreuhaender] =
    useState(false);
  const [setOnboardingcodeForTreuhaender, setSetOnboardingcodeForTreuhaender] =
    useState(false);
  const [
    sendQuestionnaireConfirmationModalVisible,
    setSendQuestionnaireConfirmationModalVisible,
  ] = useState(false);

  const [validationData, setValidationData] =
    useState<ValidateAndGetLandwirtData | null>(null);
  const [activeKey, setActiveKey] = useQueryParam(
    "tab",
    withDefault(StringParam, LWDetailsTabs.Kundeninfo)
  );
  const [getEnumInfo] = useLazyGetEnumInfoQuery();
  const [historyActionValues, setHistoryActionValues] = useState<
    Record<string, number>
  >({});

  const getEnum = async (
    name: string,
    setter: React.Dispatch<React.SetStateAction<Record<string, number>>>
  ) => {
    try {
      const payload = await getEnumInfo({
        enumName: name,
      }).unwrap();
      if (payload) {
        setter(payload);
      }
    } catch (error) {
      console.error("GetEnumInfo Error", error);
    }
  };

  const {
    data: landwirtKundensatzData,
    isLoading: landwirtKundensatzIsLoading,
    isFetching: landwirtKundensatzIsFetching,
    refetch: refetchLandwirtKundensatz,
  } = useGetLandwirtKundensatzQuery({ kundennummer });

  const {
    data: erhebungsdatenData,
    isLoading: erhebungsdatenIsLoading,
    isFetching: erhebungsdatenIsFetching,
    refetch: refetchErhebungsdaten,
  } = useGetErhebungsdatenPersonDataQuery({ kundennummer });

  const {
    data: treuhandstelleData,
    isLoading: treuhandstelleIsLoading,
    isFetching: treuhandstelleIIsFetching,
    refetch: refetchTreuhandstelle,
  } = useGetTreuhandstelleQuery({ kundennummer });

  const {
    data: treuhaenderData,
    isLoading: treuhaenderIsLoading,
    isFetching: treuhaenderIsFetching,
    refetch: refetchTreuhaender,
  } = useGetTreuhaenderQuery({ kundennummer });

  const [sendAccessData, sendAccessDataResult] = useSendAccessDataMutation();
  const [sendAkbLetter, sendAkbLetterResult] = useSendAkbLetterMutation();
  const [sendBlFbLetter, sendBlFbLetterResult] = useSendBlFbLetterMutation();
  const [resetKundensatz, resetKundensatzResult] = useResetKundensatzMutation();
  const [setNewOnboardingCode, setNewOnboardingCodeResult] =
    useSetOnboardingCodeMutation();

  useEffect(() => {
    getEnum("HistoryActionEnum", setHistoryActionValues);
    refetchLandwirtKundensatz();
    refetchTreuhaender();
    refetchTreuhandstelle();
    refetchErhebungsdaten();
  }, []);

  useEffect(() => {
    if (defaultActiveTab) {
      setActiveKey(defaultActiveTab);
    }
  }, [defaultActiveTab]);

  useEffect(() => {
    switch (modalOpenedName) {
      case LWDetailsModals.LandwirtForm:
        setLandwirtFormVisible(true);
        break;
      case LWDetailsModals.THInfos:
        setTHInfosModalVisible(true);
        break;
      case LWDetailsModals.THsInfos:
        setTHsInfosModalVisible(true);
        break;
      case LWDetailsModals.LWLanguageFormModal:
        setLWLanguageFormVisible(true);
        break;
      default:
        break;
    }
  }, [modalOpenedName]);

  useSendAccessDataMutation();

  const isRekruited =
    landwirtKundensatzData && landwirtKundensatzData.rekrutierungsstand > 1;

  const isChangeDatenlieferantEnabled =
    landwirtKundensatzData &&
    (landwirtKundensatzData.rekrutierungsstand > 1 ||
      landwirtKundensatzData.rekStatus === 34 ||
      landwirtKundensatzData.rekStatus === 18);

  const isStateChangeBtnEnabled =
    landwirtKundensatzData &&
    [22, 29].includes(landwirtKundensatzData.rekStatus) === false;

  const isNackkodierungBtnEnabled =
    landwirtKundensatzData &&
    [13, 17].includes(landwirtKundensatzData.rekStatus);

  const withTreuhandstelle =
    !isEmpty(treuhandstelleData) && !values(treuhandstelleData).every(isEmpty);

  const withTreuhandmitarbeiter =
    !isEmpty(treuhaenderData) && !values(treuhaenderData).every(isEmpty);

  const openValidationModal = () => {
    setValidationModalVisible(true);
  };

  const openLandwirtFormModal = () => {
    setLandwirtFormVisible(true);
  };

  const openTHInfosModal = () => {
    setTHInfosModalVisible(true);
  };

  const openTHsInfosModal = () => {
    setTHsInfosModalVisible(true);
  };

  const openLWLanguageFormModal = () => {
    setLWLanguageFormVisible(true);
  };

  const openChangeTHsModal = () => {
    setChangeTHsModalVisible(true);
  };

  const openChangeLieferterminModal = () => {
    setChangeLieferterminModalVisible(true);
  };

  const openRekSubStatusModal = () => {
    setRekSubStatusModalVisible(true);
  };

  const openNachkodierungsModal = () => {
    setNachkodierungModalVisible(true);
  };

  const openChangeTHContactEmailModal = () => {
    setChangeTHContactMailVisible(true);
  };

  const openResetConfirmationModal = () => {
    setResetConfirmationModalVisible(true);
  };

  const openSendAccessDataConfirmationModal = (forTreuhaender: boolean) => {
    if (sendAccessDataForTreuhaender !== forTreuhaender) {
      setSendAccessDataForTreuhaender(forTreuhaender);
    }
    setSendAccessDataConfirmationModalVisible(true);
  };

  const openSetOnboardingcodeForTreuhaenderConfirmationModal = (
    forTreuhaender: boolean
  ) => {
    if (setOnboardingcodeForTreuhaender !== forTreuhaender) {
      setSetOnboardingcodeForTreuhaender(forTreuhaender);
    }
    setSetNewOnboardingCodeLwModalVisible(true);
  };

  const openSendAkbLetterConfirmationModal = () => {
    setSendAkbLetterConfirmationModalVisible(true);
  };

  const openSendQuestionnaireConfirmationModal = () => {
    setSendQuestionnaireConfirmationModalVisible(true);
  };

  const refetchAll = () => {
    refetchLandwirtKundensatz();
    refetchTreuhandstelle();
    refetchTreuhaender();
  };

  const confirmSendAccessData = async () => {
    try {
      const response = await sendAccessData({
        kundennummer,
        forTreuhaender: sendAccessDataForTreuhaender,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.send_access_data_success"),
        });
        setSendAccessDataConfirmationModalVisible(false);
      } else {
        notification.error({ message: response.errorMessage });
        setSendAccessDataConfirmationModalVisible(false);
      }
    } catch (error) {
      console.error("Error", error);
      setSendAccessDataConfirmationModalVisible(false);
    }
  };

  const confirmSendAkbLetter = async () => {
    try {
      const response = await sendAkbLetter({
        kundennummer,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.send_akb_letter_success"),
        });
        setSendAkbLetterConfirmationModalVisible(false);
      } else {
        notification.error({ message: response.errorMessage });
        setSendAkbLetterConfirmationModalVisible(false);
      }
    } catch (error) {
      console.error("Error", error);
      setSendAkbLetterConfirmationModalVisible(false);
    }
  };

  const confirmSendQuestionnaire = async () => {
    try {
      const response = await sendBlFbLetter({
        kundennummer,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.send_questionnaire_success"),
        });
        setSendQuestionnaireConfirmationModalVisible(false);
      } else {
        notification.error({ message: response.errorMessage });
        setSendQuestionnaireConfirmationModalVisible(false);
      }
    } catch (error) {
      console.error("Error", error);
      setSendQuestionnaireConfirmationModalVisible(false);
    }
  };

  const confirmResetKundensatz = async () => {
    try {
      const response = await resetKundensatz({
        kundennummer,
      }).unwrap();
      if (!response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.reset_recruitment_success"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
      setResetConfirmationModalVisible(false);
      refetchAll();
    } catch (error) {
      console.error("Error", error);
    }
  };

  const confirmSetNewOnboardingCode = async () => {
    try {
      const response = await setNewOnboardingCode({
        kundennummer,
        datenlieferant: setOnboardingcodeForTreuhaender ? 2 : 1,
      }).unwrap();
      if (response && !response.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.set_new_onboardingcode_success"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setSetNewOnboardingCodeLwModalVisible(false);
    }
  };

  const onChangeLieferterminSuccess = () => {
    refetchLandwirtKundensatz();
  };

  return (
    <Modal
      title={
        <Title level={4} className={styles.title}>
          Kundennummer {kundennummer} Information
        </Title>
      }
      visible={visible && defaultActiveTab !== undefined}
      onCancel={() => onClose(true)}
      maskClosable
      destroyOnClose
      footer={null}
      style={{ marginTop: "-80px" }}
      width={1850}
    >
      <Tabs activeKey={activeKey} type="card" onChange={setActiveKey}>
        <TabPane
          tab={t("masterdata.lw_details.info")}
          key={LWDetailsTabs.Kundeninfo}
        >
          <Space
            className={styles.full_width}
            direction="vertical"
            size="large"
          >
            <Row gutter={[10, 10]} wrap>
              {isBtnVisible && (
                <Col lg={6}>
                  <Button
                    icon={<SendOutlined />}
                    type="primary"
                    onClick={() => openSendAkbLetterConfirmationModal()}
                    block
                  >
                    {t("masterdata.lw_details.send_akb_letter")}
                  </Button>
                </Col>
              )}
              {isBtnVisible && (
                <Col lg={6}>
                  <Button
                    icon={<SendOutlined />}
                    type="primary"
                    onClick={() => openSendAccessDataConfirmationModal(false)}
                    disabled={!isRekruited}
                    block
                  >
                    {t("masterdata.lw_details.send_access_data_lw")}
                  </Button>
                </Col>
              )}
              {isBtnVisible && landwirtKundensatzData?.datenlieferant === 2 && (
                <Col lg={6}>
                  <Button
                    icon={<SendOutlined />}
                    onClick={() => openSendAccessDataConfirmationModal(true)}
                    disabled={!isRekruited}
                    type="primary"
                    block
                  >
                    {t("masterdata.lw_details.send_access_data_th")}
                  </Button>
                </Col>
              )}
              {isBtnVisible && landwirtKundensatzData?.datenlieferant === 2 && (
                <Col lg={8}>
                  <Button
                    icon={<SendOutlined />}
                    onClick={() => openSendQuestionnaireConfirmationModal()}
                    disabled={!isRekruited}
                    type="primary"
                    block
                  >
                    {t("masterdata.lw_details.send_questionnaire")}
                  </Button>
                </Col>
              )}
              {isAdminBtnVisible && (
                <Col lg={6}>
                  <Button
                    icon={<SwapOutlined />}
                    type="primary"
                    onClick={() =>
                      openSetOnboardingcodeForTreuhaenderConfirmationModal(
                        false
                      )
                    }
                    disabled={!isRekruited}
                    block
                  >
                    {t("masterdata.lw_details.set_new_onboardingcode_lw")}
                  </Button>
                </Col>
              )}
              {isAdminBtnVisible &&
                landwirtKundensatzData?.datenlieferant === 2 && (
                  <Col lg={6}>
                    <Button
                      icon={<SwapOutlined />}
                      onClick={() =>
                        openSetOnboardingcodeForTreuhaenderConfirmationModal(
                          true
                        )
                      }
                      disabled={!isRekruited}
                      type="primary"
                      block
                    >
                      {t("masterdata.lw_details.set_new_onboardingcode_th")}
                    </Button>
                  </Col>
                )}
              {isSprachwechselBtnVisible && (
                <Col lg={5}>
                  <Button
                    icon={<GlobalOutlined />}
                    type="primary"
                    onClick={openLWLanguageFormModal}
                    block
                  >
                    {t("masterdata.lw_details.change_language")}
                  </Button>
                </Col>
              )}
              {isBtnVisible && (
                <Col lg={5}>
                  <Button
                    icon={<UserSwitchOutlined />}
                    onClick={openChangeTHsModal}
                    disabled={!isChangeDatenlieferantEnabled}
                    type="primary"
                    block
                  >
                    {t("masterdata.lw_details.add_change_ths")}
                  </Button>
                </Col>
              )}
              {isBtnVisible && (
                <Col lg={5}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={openRekSubStatusModal}
                    type="primary"
                    block
                    disabled={!isStateChangeBtnEnabled}
                  >
                    {t("masterdata.lw_details.state_change")}
                  </Button>
                </Col>
              )}
              {isBtnNachkodierungVisible && (
                <Col lg={5}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={openNachkodierungsModal}
                    type="primary"
                    block
                    disabled={!isNackkodierungBtnEnabled}
                  >
                    {t("masterdata.lw_details.nachkodierung")}
                  </Button>
                </Col>
              )}
              {isBtnVisible && (
                <Col lg={6}>
                  <Button
                    icon={<CalendarOutlined />}
                    onClick={openChangeLieferterminModal}
                    disabled={!isRekruited}
                    type="primary"
                    block
                  >
                    {t("masterdata.lw_details.change_delivery_date")}
                  </Button>
                </Col>
              )}
              {isBtnVisible && withTreuhandstelle && (
                <Col lg={6}>
                  <Button
                    icon={<MailOutlined />}
                    onClick={openChangeTHContactEmailModal}
                    disabled={!isRekruited}
                    type="primary"
                    block
                  >
                    {t("masterdata.lw_details.change_th_email")}
                  </Button>
                </Col>
              )}
              {isAdminBtnVisible && (
                <Col lg={6}>
                  <Button
                    icon={<UndoOutlined />}
                    onClick={openResetConfirmationModal}
                    type="primary"
                    block
                  >
                    {t("masterdata.lw_details.reset_recruitment")}
                  </Button>
                </Col>
              )}
            </Row>
            <Skeleton
              className={styles.full_width}
              active
              loading={erhebungsdatenIsLoading || erhebungsdatenIsFetching}
            >
              {erhebungsdatenData ? (
                <Descriptions
                  title={
                    <Row align="middle" gutter={20}>
                      <Col>
                        <span className="ant-descriptions-title">
                          {t("masterdata.lw_details.datenlieferant_title")}
                        </span>
                      </Col>
                    </Row>
                  }
                >
                  <Descriptions.Item label={t("masterdata.lw_details.email")}>
                    {erhebungsdatenData?.email}
                  </Descriptions.Item>
                </Descriptions>
              ) : null}
            </Skeleton>
            <Skeleton
              className={styles.full_width}
              active
              loading={
                landwirtKundensatzIsLoading || landwirtKundensatzIsFetching
              }
            >
              <Descriptions
                title={
                  <Row align="middle" gutter={20}>
                    <Col>
                      <span className="ant-descriptions-title">
                        {t("masterdata.lw_details.farmer")}
                      </span>
                    </Col>
                    {isBtnVisible && (
                      <Col>
                        <Popover
                          content={t(
                            "masterdata.lw_details.edit_customer_infos"
                          )}
                        >
                          <Button
                            type="ghost"
                            icon={<EditTwoTone twoToneColor="#e30613" />}
                            onClick={openLandwirtFormModal}
                          />
                        </Popover>
                      </Col>
                    )}
                  </Row>
                }
                extra={
                  <Space>
                    {!landwirtKundensatzData?.isValidated && (
                      <Button
                        type="ghost"
                        icon={<CheckCircleTwoTone twoToneColor="#e30613" />}
                        onClick={openValidationModal}
                      >
                        {t("masterdata.lw_details.validate")}
                      </Button>
                    )}
                    <Button
                      type="ghost"
                      onClick={() => {
                        if (landwirtKundensatzData?.zammadTicketsLink) {
                          openLinkInNewTab(
                            landwirtKundensatzData.zammadTicketsLink
                          );
                        }
                      }}
                      icon={<LinkOutlined />}
                      disabled={!landwirtKundensatzData?.zammadTicketsLink}
                    >
                      {landwirtKundensatzData?.zammadTicketsLink
                        ? t("masterdata.lw_details.all_tickets")
                        : t("no_tickets")}
                    </Button>
                  </Space>
                }
              >
                <Descriptions.Item
                  label={t("masterdata.lw_details.kundennummer")}
                >
                  {landwirtKundensatzData?.kundennummer}
                </Descriptions.Item>
                {(validationData || landwirtKundensatzData?.isValidated) && (
                  <>
                    <Descriptions.Item
                      label={t("masterdata.lw_details.firstname")}
                    >
                      {landwirtKundensatzData?.firstname ||
                        validationData?.result.firstname}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("masterdata.lw_details.lastname")}
                    >
                      {landwirtKundensatzData?.lastname ||
                        validationData?.result.lastname}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("masterdata.lw_details.email")}>
                      {landwirtKundensatzData?.email ||
                        validationData?.result.email}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("masterdata.lw_details.phone")}>
                      {landwirtKundensatzData?.primaryPhone ||
                        validationData?.result.primaryPhone ||
                        "-"}
                      <br />
                      {landwirtKundensatzData?.phone2 ||
                        validationData?.result.phone2 ||
                        "-"}
                      <br />
                      {landwirtKundensatzData?.phone3 ||
                        validationData?.result.phone3 ||
                        "-"}
                      <br />
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("masterdata.lw_details.adresse")}
                    >
                      {landwirtKundensatzData?.address ||
                        validationData?.result.address}{" "}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("masterdata.lw_details.ort")}>
                      {landwirtKundensatzData?.postalCode ||
                        validationData?.result.postalCode}{" "}
                      {landwirtKundensatzData?.place ||
                        validationData?.result.place}
                    </Descriptions.Item>
                  </>
                )}
                <Descriptions.Item
                  label={t("masterdata.lw_details.betriebstyp")}
                >
                  {!isNil(landwirtKundensatzData?.betriebstypDetailed) &&
                    t(
                      `masterdata.enums.BetriebstypDetailedEnum.${landwirtKundensatzData?.betriebstypDetailed}`
                    )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.language_farmer")}
                >
                  {!isNil(landwirtKundensatzData?.languageLandwirt) &&
                    t(
                      `masterdata.enums.LanguageEnum.${landwirtKundensatzData?.languageLandwirt}`
                    )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.rek_status")}
                >
                  {!isNil(landwirtKundensatzData?.rekStatus) &&
                    t(
                      `masterdata.enums.RekStatusEnum.${landwirtKundensatzData?.rekStatus}`
                    )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.rek_sub_status")}
                >
                  {!isNil(landwirtKundensatzData?.rekSubStatus) &&
                    t(
                      `masterdata.enums.RekSubStatusEnum.${landwirtKundensatzData?.rekSubStatus}`
                    )}
                </Descriptions.Item>

                <Descriptions.Item label={t("masterdata.lw_details.iban")}>
                  {landwirtKundensatzData?.iban}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.dk_status")}>
                  {landwirtKundensatzData?.datenkontrolleString}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.entschaedigung")}
                >
                  {t(`${landwirtKundensatzData?.entschaedigung}`)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.abgeschlossenBl")}
                >
                  {t(`${landwirtKundensatzData?.abgeschlossenBl}`)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.abgeschlossenThLw")}
                >
                  {t(`${landwirtKundensatzData?.abgeschlossenThLw}`)}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.brm")}>
                  {t(`${landwirtKundensatzData?.brm}`)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.language_brm")}
                >
                  {!isNil(landwirtKundensatzData?.languageBRM) &&
                    t(
                      `masterdata.enums.LanguageEnum.${landwirtKundensatzData?.languageBRM}`
                    )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.datenlieferant")}
                >
                  {!isNil(landwirtKundensatzData?.datenlieferant) &&
                    t(
                      `masterdata.enums.DatenlieferantEnum.${landwirtKundensatzData?.datenlieferant}`
                    )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.delivery_termin")}
                >
                  {!isNil(landwirtKundensatzData?.liefertermin) &&
                    landwirtKundensatzData &&
                    formatDateTime({
                      date: new Date(landwirtKundensatzData.liefertermin),
                      withoutTime: true,
                      isLocalTimezone: true,
                    })}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.recruitment_status")}
                >
                  {!isNil(landwirtKundensatzData?.rekrutierungsstand) &&
                    t(
                      `masterdata.enums.RekrutierungsstandEnum.${landwirtKundensatzData?.rekrutierungsstand}`
                    )}
                </Descriptions.Item>
              </Descriptions>
            </Skeleton>
            <Skeleton
              className={styles.full_width}
              active
              loading={treuhandstelleIsLoading || treuhandstelleIIsFetching}
            >
              {withTreuhandstelle ? (
                <Descriptions
                  title={
                    <Row align="middle" gutter={20}>
                      <Col>
                        <span className="ant-descriptions-title">
                          {t("masterdata.lw_details.treuhandstelle")}
                        </span>
                      </Col>
                      <Col>
                        <Popover
                          content={t("masterdata.lw_details.see_ths_infos")}
                        >
                          <Button
                            type="ghost"
                            icon={<EyeTwoTone twoToneColor="#e30613" />}
                            onClick={openTHsInfosModal}
                          />
                        </Popover>
                      </Col>
                    </Row>
                  }
                  extra={
                    <Space>
                      <Button
                        type="ghost"
                        onClick={() => {
                          if (treuhandstelleData?.zammadTicketsLink) {
                            openLinkInNewTab(
                              treuhandstelleData.zammadTicketsLink
                            );
                          }
                        }}
                        icon={<LinkOutlined />}
                        disabled={!treuhandstelleData?.zammadTicketsLink}
                      >
                        {treuhandstelleData?.zammadTicketsLink
                          ? t("masterdata.lw_details.all_tickets")
                          : t("no_tickets")}
                      </Button>
                    </Space>
                  }
                >
                  <Descriptions.Item label={t("masterdata.lw_details.id")}>
                    {treuhandstelleData?.extId}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.name")}>
                    {treuhandstelleData?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.adresse")}>
                    {treuhandstelleData?.address}
                    {treuhandstelleData?.addressAddition}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.ort")}>
                    {treuhandstelleData?.postalCode} {treuhandstelleData?.place}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.email")}>
                    {treuhandstelleData?.email}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.phone")}>
                    {treuhandstelleData?.phone}
                  </Descriptions.Item>
                </Descriptions>
              ) : null}
            </Skeleton>
            <Skeleton
              className={styles.full_width}
              active
              loading={treuhaenderIsLoading || treuhaenderIsFetching}
            >
              {withTreuhandmitarbeiter ? (
                <Descriptions
                  title={
                    <Row align="middle" gutter={20}>
                      <Col>
                        <span className="ant-descriptions-title">
                          {t("masterdata.lw_details.treuhaender")}
                        </span>
                      </Col>
                      <Col>
                        <Popover
                          content={t("masterdata.lw_details.see_th_infos")}
                        >
                          <Button
                            type="ghost"
                            icon={<EyeTwoTone twoToneColor="#e30613" />}
                            onClick={openTHInfosModal}
                          />
                        </Popover>
                      </Col>
                    </Row>
                  }
                  extra={
                    <Space>
                      <Button
                        type="ghost"
                        onClick={() => {
                          if (treuhaenderData?.zammadTicketsLink) {
                            openLinkInNewTab(treuhaenderData.zammadTicketsLink);
                          }
                        }}
                        icon={<LinkOutlined />}
                        disabled={!treuhaenderData?.zammadTicketsLink}
                      >
                        {treuhaenderData?.zammadTicketsLink
                          ? t("masterdata.lw_details.all_tickets")
                          : t("no_tickets")}
                      </Button>
                    </Space>
                  }
                >
                  <Descriptions.Item
                    label={t("masterdata.lw_details.lastname")}
                  >
                    {treuhaenderData?.lastname} ({treuhaenderData?.id})
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("masterdata.lw_details.firstname")}
                  >
                    {treuhaenderData?.firstname}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.adresse")}>
                    {treuhaenderData?.address}
                    {treuhaenderData?.addressAddition}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.ort")}>
                    {treuhaenderData?.postalCode} {treuhaenderData?.place}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("masterdata.lw_details.phone")}>
                    {treuhaenderData?.primaryPhone || ""}
                    <br />
                    {treuhaenderData?.phone2 || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("masterdata.lw_details.language_treuhander")}
                  >
                    {!isNil(treuhaenderData?.language) &&
                      t(
                        `masterdata.enums.LanguageEnum.${treuhaenderData?.language}`
                      )}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("masterdata.lw_details.dispatch_email_address")}
                  >
                    {landwirtKundensatzData?.treuhaenderContactMail}
                  </Descriptions.Item>
                </Descriptions>
              ) : null}
            </Skeleton>
            <Row justify="center">
              <Button
                onClick={() => {
                  if (landwirtKundensatzData?.zammadCreateTicketLink) {
                    openLinkInNewTab(
                      landwirtKundensatzData.zammadCreateTicketLink
                    );
                  }
                }}
                disabled={!landwirtKundensatzData?.zammadCreateTicketLink}
                icon={<FileAddTwoTone twoToneColor="#e30613" />}
              >
                {t("masterdata.lw_details.new_ticket")}
              </Button>
            </Row>
          </Space>
          {landwirtKundensatzData && treuhaenderData && treuhandstelleData ? (
            <>
              <ValidationForm
                initialValues={{
                  kundennummer,
                  firstname: landwirtKundensatzData.firstname,
                  lastname: landwirtKundensatzData.lastname,
                  email: landwirtKundensatzData.email,
                  phone: landwirtKundensatzData.primaryPhone,
                  postalCode: landwirtKundensatzData.postalCode,
                  setValidated: true,
                }}
                visible={validationModalVisible}
                onClose={() => setValidationModalVisible(false)}
                onSuccess={(data) => {
                  setValidationData(data);
                }}
              />
              <UpdateLandwirtForm
                initialValues={{
                  kundennummer,
                  id: landwirtKundensatzData.idLandwirt,
                  firstname: landwirtKundensatzData.firstname,
                  lastname: landwirtKundensatzData.lastname,
                  email: landwirtKundensatzData.email,
                  primaryPhone: landwirtKundensatzData.primaryPhone,
                  phone2: landwirtKundensatzData.phone2,
                  phone3: landwirtKundensatzData.phone3,
                  address: landwirtKundensatzData.address,
                  place: landwirtKundensatzData.place,
                  postalCode: landwirtKundensatzData.postalCode,
                  iban: landwirtKundensatzData.iban,
                  ibanName: landwirtKundensatzData.ibanName,
                  ibanAddress: landwirtKundensatzData.ibanAddress,
                  ibanZipCode: landwirtKundensatzData.ibanZipCode,
                  ibanTown: landwirtKundensatzData.ibanTown,
                  ibanComment: landwirtKundensatzData.ibanComment,
                  ibanOption: landwirtKundensatzData.ibanOption,
                }}
                visible={landwirtFormVisible}
                onClose={() => setLandwirtFormVisible(false)}
                onSuccess={() => refetchLandwirtKundensatz()}
              />
              <THInfoModal
                visible={tHInfoModalVisible}
                kundennummer={kundennummer}
                treuhaenderId={treuhaenderData.id}
                rekStatusFilters={rekStatusFilters || []}
                onClose={() => setTHInfosModalVisible(false)}
              />
              <THsInfoModal
                visible={tHsInfoModalVisible}
                refetchTreuhandstelleData={refetchTreuhandstelle}
                treuhandstelleData={treuhandstelleData}
                rekStatusFilters={rekStatusFilters || []}
                onClose={() => setTHsInfosModalVisible(false)}
              />
              <LWLanguageForm
                initialValues={{
                  id: landwirtKundensatzData.idLandwirt,
                  language: landwirtKundensatzData.languageLandwirt,
                }}
                visible={lWLanguageFormVisible}
                onClose={() => setLWLanguageFormVisible(false)}
                onSuccess={() => refetchLandwirtKundensatz()}
              />
              {changeTHsModalVisible && (
                <ChangeTHsModal
                  visible={changeTHsModalVisible}
                  kundennummer={landwirtKundensatzData.kundennummer}
                  datenlieferant={landwirtKundensatzData.datenlieferant}
                  onClose={() => setChangeTHsModalVisible(false)}
                  onSuccess={refetchAll}
                />
              )}
              {changeLieferterminModalVisible && (
                <ChangeLieferterminModal
                  initialValue={landwirtKundensatzData.liefertermin}
                  kundennummer={landwirtKundensatzData.kundennummer}
                  visible={changeLieferterminModalVisible}
                  onClose={() => setChangeLieferterminModalVisible(false)}
                  onSuccess={onChangeLieferterminSuccess}
                />
              )}
              {changeTHContactMailVisible && (
                <ChangeTHContactEmail
                  initialValues={{
                    email: landwirtKundensatzData.treuhaenderContactMail,
                  }}
                  kundennummer={landwirtKundensatzData.kundennummer}
                  visible={changeTHContactMailVisible}
                  onClose={() => setChangeTHContactMailVisible(false)}
                  onSuccess={() => refetchLandwirtKundensatz()}
                />
              )}
              {rekSubStatusModalVisible && (
                <SetRekSubStatusModal
                  visible={rekSubStatusModalVisible}
                  onClose={() => setRekSubStatusModalVisible(false)}
                  kundennummer={kundennummer}
                  currentRekStatus={landwirtKundensatzData.rekStatus}
                  currentRekSubStatus={landwirtKundensatzData.rekSubStatus}
                  ausfallReason={landwirtKundensatzData.ausfallReason}
                  onSuccess={() => refetchLandwirtKundensatz()}
                  isNachkodierung={false}
                />
              )}
              {nachkodierungModalVisible && (
                <SetRekSubStatusModal
                  visible={nachkodierungModalVisible}
                  onClose={() => setNachkodierungModalVisible(false)}
                  kundennummer={kundennummer}
                  currentRekStatus={landwirtKundensatzData.rekStatus}
                  currentRekSubStatus={landwirtKundensatzData.rekSubStatus}
                  ausfallReason={landwirtKundensatzData.ausfallReason}
                  onSuccess={() => refetchLandwirtKundensatz()}
                  isNachkodierung
                />
              )}
              <ConfirmationModal
                visible={sendAccessDataConfirmationModalVisible}
                isLoading={sendAccessDataResult.isLoading}
                title={t(
                  "masterdata.lw_details.send_access_data_confirmation_title"
                )}
                description={t(
                  "masterdata.lw_details.send_access_data_confirmation"
                )}
                onClose={() => setSendAccessDataConfirmationModalVisible(false)}
                onConfirm={() => confirmSendAccessData()}
              />
              <ConfirmationModal
                visible={sendAkbLetterConfirmationModalVisible}
                isLoading={sendAkbLetterResult.isLoading}
                title={t(
                  "masterdata.lw_details.send_akb_letter_confirmation_title"
                )}
                description={t(
                  "masterdata.lw_details.send_akb_letter_confirmation"
                )}
                onClose={() => setSendAkbLetterConfirmationModalVisible(false)}
                onConfirm={() => confirmSendAkbLetter()}
              />
              <ConfirmationModal
                visible={sendQuestionnaireConfirmationModalVisible}
                isLoading={sendBlFbLetterResult.isLoading}
                title={t(
                  "masterdata.lw_details.send_questionnaire_confirmation_title"
                )}
                description={t(
                  "masterdata.lw_details.send_questionnaire_confirmation"
                )}
                onClose={() =>
                  setSendQuestionnaireConfirmationModalVisible(false)
                }
                onConfirm={() => confirmSendQuestionnaire()}
              />
              <ConfirmationModal
                visible={resetConfirmationModalVisible}
                isLoading={resetKundensatzResult.isLoading}
                title={t("masterdata.lw_details.reset_recruitment")}
                description={t(
                  "masterdata.lw_details.reset_recruitment_confirmation"
                )}
                onClose={() => setResetConfirmationModalVisible(false)}
                onConfirm={() => confirmResetKundensatz()}
              />
              <ConfirmationModal
                visible={setNewOnboardingCodeLwModalVisible}
                isLoading={setNewOnboardingCodeResult.isLoading}
                title={t(
                  `masterdata.lw_details.set_new_onboardingcode_${
                    setOnboardingcodeForTreuhaender ? "th" : "lw"
                  }`
                )}
                description={t(
                  "masterdata.lw_details.set_new_onboardingcode_confirmation"
                )}
                onClose={() => setSetNewOnboardingCodeLwModalVisible(false)}
                onConfirm={() => confirmSetNewOnboardingCode()}
              />
            </>
          ) : null}
        </TabPane>
        <TabPane
          tab={t("masterdata.lw_details.history")}
          key={LWDetailsTabs.History}
        >
          {landwirtKundensatzData ? (
            <HistoryTab
              kundennummer={landwirtKundensatzData.kundennummer}
              refetchHistoryTab={false}
              historyActionValues={historyActionValues}
            />
          ) : null}
        </TabPane>
      </Tabs>
    </Modal>
  );
}

export default LWDetailsModal;
